import cn from "classnames";
import {
    AddressInput,
    BidDataFilter,
    BidRoadRestrictions,
    EmergencyAreaPoints,
    FixRouteBidBtn,
    FixRouteBidForm,
    Map as LeafletMap,
    MapControl,
    MarkerCluster,
    PaymentPoints,
    PlatonPoints,
    PointsFiltersMap,
    Postamats,
    RegionDryingPeriods,
    RoadRestrictions,
    RoadRestrictionsList,
    RouteControlPoints,
    UnstableZones,
} from "components/map/shared";
import Loader from "components/shared/loader";
import { isStartBidPoint, START_POINT_LETTER } from "helpers/bidpoint";
import { bidPointTypesEnum, gasStationTypesEnum, mapObjectTypesEnum, routeTypesEnum } from "helpers/enums";
import {
    clearGasStations,
    id,
    planDashLineStyle,
    planLineStyle,
    suggestedRouteStyle,
    taskMarkerStyle,
} from "helpers/map";
import { isRegionDryingPeriodsEnabledForClients } from "helpers/regionDryingPeriods";
import { canManageOrganization, pointLetter } from "helpers/system";
import { divIcon, polyline } from "leaflet";
import debounce from "lodash/debounce";
import React from "react";
import { CircleMarker, Marker, Polyline } from "react-leaflet";
import { useMap, useMapEvent } from "react-leaflet/hooks";
import { connect } from "react-redux";
import { alert } from "services/alert";
import { geocoder, getRoute } from "sources/map";
import { getDefaultBidDataFilters } from "./shared/pointsFiltersMap";

const routeThroughPointErrorMessage = isRegionDryingPeriodsEnabledForClients
    ? "Невозможно построить маршрут через выбранную точку. Скорректируйте настройки сезонных ограничений."
    : "Невозможно построить маршрут через выбранную точку. Проверьте точку и настройки маршрута.";

const idifyCoords = (data) => {
    if (!data) {
        return false;
    }
    const { concatedCoords, routeInfo, roadRestrictions, roadControlPoints } = data;
    const itemId = id();
    return {
        coords: concatedCoords,
        routeInfo: {
            ...routeInfo,
            tollSegments: routeInfo.tollSegments?.map((item) => ({ ...item, segmentIndex: itemId })),
        },
        roadRestrictions,
        roadControlPoints: roadControlPoints || [],
        i: itemId,
    };
};

class CreateRoute extends PointsFiltersMap {
    state = {
        geopoint: { location: { coordinates: [] } },
        isLoading: false,
        polylinePoint: null,
        bidFilters: getDefaultBidDataFilters(),
    };

    polylinePointDraged = false;
    polylinePointSegmentId = null;
    lines = {};

    constructor(props) {
        super(props);
        this.loadRoutesPreview = debounce(this.loadRoutesPreview, 100);
        this.onPolylineMouseOut = debounce(this.onPolylineMouseOut, 10);
    }

    componentDidMount() {
        this.updateMapPosition();
    }

    UNSAFE_componentWillReceiveProps() {
        this.removeSuggestedRoutes();
    }

    updateMapPosition = () => {
        let elements = [];
        if (this.props.segments.length) {
            const coords = this.props.segments.reduce((prev, cur) => prev.concat(cur.coords), []);
            elements = elements.concat(coords);
        }
        if (this.props.markers.length) {
            const coords = this.props.markers.reduce((prev, cur) => {
                const location = cur.geozone?.location?.coordinates;
                prev.push([location[1], location[0]]);
                return prev;
            }, []);
            elements = elements.concat(coords);
        }
        this.changeMapPosition(elements);
    };

    componentDidUpdate(prevProps) {
        const emptyPrevSegments = (prevProps.segments && !prevProps.segments.length) || !prevProps.segments;
        const emptyPrevMarkers = (prevProps.markers && !prevProps.markers.length) || !prevProps.markers;
        if (
            (emptyPrevSegments && this.props.segments && this.props.segments.length) ||
            (emptyPrevMarkers && this.props.markers && this.props.markers.length) ||
            this.props.changeMapPosition
        ) {
            this.updateMapPosition();
        }
    }

    renderFixRouteBidBtn = () => (
        <FixRouteBidBtn
            startSelectRange={() =>
                this.setState({
                    selectGeozoneMode: "start",
                    startGeozone: undefined,
                    endGeozone: undefined,
                    routeToFix: undefined,
                })
            }
        />
    );

    handleMapClick = ({ latlng, isDraged }) => {
        const { shouldResize } = this.props;
        if (!shouldResize || isDraged) {
            this.setState({ isLoading: true });
            geocoder(latlng).then((geopoint) => {
                if (geopoint) {
                    this.setState({ isLoading: false, geopoint });
                    if (isDraged) {
                        this.addGeopoint(geopoint, isDraged);
                    }
                } else {
                    this.setState({ isLoading: false });
                }
            });
        }
    };

    removeSuggestedRoutes = () => {
        this.suggestedFirstLine && this.suggestedFirstLine.removeFrom(this.map);
        this.suggestedSecondLine && this.suggestedSecondLine.removeFrom(this.map);
    };

    loadRoutesPreview = ({ latlng }) => {
        const { navElements, routeOptions } = this.props;
        const arrLL = [latlng.lng, latlng.lat];
        const navElement = navElements.find((item) => this.polylinePointSegmentId === item.i);
        if (!navElement) {
            return;
        }
        let start;
        let end;
        if (navElement.panelType === "segment") {
            start = navElement.geozone.start;
            end = navElement.geozone.end;
        } else {
            const elemIndex = navElements.indexOf(navElement);
            start = navElements[elemIndex - 2].geozone;
            end = navElements[elemIndex + 2].geozone;
        }
        const firstSegment = getRoute([start.location.coordinates, arrLL], { silent: true, routeOptions });
        const secondSegment = getRoute([arrLL, end.location.coordinates], { silent: true, routeOptions });
        Promise.all([firstSegment, secondSegment]).then((res) => {
            const [first, second] = res;
            if (first && second) {
                this.removeSuggestedRoutes();
                setTimeout(() => {
                    this.suggestedFirstLine = polyline(first.concatedCoords, suggestedRouteStyle);
                    this.suggestedSecondLine = polyline(second.concatedCoords, suggestedRouteStyle);
                    this.suggestedFirstLine.addTo(this.map);
                    this.suggestedSecondLine.addTo(this.map);
                    this.suggestedFirstLine.bringToBack();
                    this.suggestedSecondLine.bringToBack();
                }, 0);
            }
        });
    };

    addGeopoint = (geopoint, isDraged) => {
        const arrLL = geopoint.location.coordinates;
        const {
            activeElements,
            changeActiveElements,
            shouldResize,
            mapClickDisabled,
            disableMapClick,
            routeOptions,
            tollRoadSegments,
        } = this.props;
        let { navElements, segments, markers } = this.props;
        if (!shouldResize || isDraged) {
            if (mapClickDisabled && !isDraged) {
                return;
            }
            disableMapClick();

            const activeSegments = [];
            if (activeElements.segments) {
                activeSegments.push(...activeElements.segments);
            }
            if (this.polylinePointSegmentId) {
                activeSegments.push(this.polylinePointSegmentId);
            }
            let activeSegmentId = activeSegments[0];

            const newElements = clearGasStations({ elements: navElements, markers, segments }, activeSegmentId);
            markers = newElements.markers;
            segments = newElements.segments;
            navElements = newElements.elements;
            if (newElements.activeSegment) {
                activeSegmentId = newElements.activeSegment.i;
            }

            const {
                geozone: { start, end },
                routeType,
            } = navElements.find((item) => activeSegmentId === item.i);

            const firstSegment = getRoute([start.location.coordinates, arrLL], { silent: true, routeOptions }).then(
                idifyCoords,
            );
            const secondSegment = getRoute([arrLL, end.location.coordinates], { silent: true, routeOptions }).then(
                idifyCoords,
            );

            this.setState({ isLoading: true });
            Promise.all([firstSegment, secondSegment]).then((res) => {
                const [first, second] = res;

                if (!first || !second) {
                    alert({
                        message: routeThroughPointErrorMessage,
                        type: "warning",
                    });
                    this.setState({
                        isLoading: false,
                        mouseInPolyline: false,
                        polylinePoint: null,
                        geopoint: { location: { coordinates: [] } },
                    });
                    return changeActiveElements({ mapClickDisabled: false });
                }
                const point = { ...geopoint, i: id() };
                const nextNavElements = [...navElements];
                const elementIndex = nextNavElements.findIndex((item) => item.i === activeSegmentId);
                const segmentIndex = segments.findIndex((segment) => segment.i === activeSegmentId);
                const markerIndex = markers.findIndex((marker) => marker.i === nextNavElements[elementIndex - 1].i);
                nextNavElements.splice(
                    elementIndex,
                    1,
                    ...[
                        {
                            panelType: "segment",
                            geozone: { start, end: point },
                            i: first.i,
                            coords: first.coords,
                            routeInfo: first.routeInfo,
                            routeType,
                        },
                        { panelType: "geopoint", geozone: point, i: point.i },
                        {
                            panelType: "segment",
                            geozone: { start: point, end },
                            i: second.i,
                            coords: second.coords,
                            routeInfo: second.routeInfo,
                            routeType,
                        },
                    ],
                );
                markers.splice(markerIndex, 0, { geozone: point, i: point.i });
                segments.splice(segmentIndex, 1, ...[first, second]);
                const nextTollRoadSegments = tollRoadSegments.filter((item) => item.segmentIndex !== activeSegmentId);
                nextTollRoadSegments.push(...(first.routeInfo.tollSegments || []));
                nextTollRoadSegments.push(...(second.routeInfo.tollSegments || []));
                const nextState = {
                    segments,
                    markers,
                    elements: nextNavElements,
                    tollRoadSegments: nextTollRoadSegments,
                    sidebarOpened: true,
                    mapClickDisabled: true,
                    routeChanged: true,
                    activeElements: { point: null, segments: null },
                };
                this.setState({ isLoading: false, polylinePoint: null, geopoint: { location: { coordinates: [] } } });
                return changeActiveElements(nextState);
            });
        }
        this.polylinePointDraged = false;
    };

    updateBetweenPoint = ({ target }, pointId) => {
        this.removeSuggestedRoutes();
        const latlng = target._latlng;
        const { changeActiveElements, disableMapClick, routeOptions, tollRoadSegments } = this.props;
        let { navElements, segments, markers } = this.props;
        const newElements = clearGasStations({ elements: navElements, markers, segments });
        markers = newElements.markers;
        segments = newElements.segments;
        navElements = newElements.elements;
        disableMapClick();
        const arrLL = [latlng.lng, latlng.lat];
        const navElement = navElements.find((item) => pointId === item.i);
        const elemIndex = navElements.indexOf(navElement);
        const start = navElements[elemIndex - 2].geozone;
        const end = navElements[elemIndex + 2].geozone;
        const geopointReq = geocoder(latlng);
        const firstSegment = getRoute([start.location.coordinates, arrLL], { silent: true, routeOptions }).then(
            idifyCoords,
        );
        const secondSegment = getRoute([arrLL, end.location.coordinates], { silent: true, routeOptions }).then(
            idifyCoords,
        );
        this.setState({ isLoading: true });
        Promise.all([geopointReq, firstSegment, secondSegment]).then((res) => {
            const [geopoint, first, second] = res;
            if (!first || !second) {
                alert({
                    message: routeThroughPointErrorMessage,
                    type: "warning",
                });
                return this.setState({ isLoading: false });
            }
            const point = { ...geopoint, i: id() };
            const nextNavElements = [...navElements];
            const segmentIndex = segments.findIndex((segment) => segment.i === navElements[elemIndex - 1].i);
            const activeSegmentId = segments[segmentIndex].i;
            const markerIndex = markers.findIndex((marker) => marker.i === navElement.i);
            nextNavElements.splice(
                elemIndex - 1,
                3,
                ...[
                    {
                        panelType: "segment",
                        geozone: { start, end: point },
                        i: first.i,
                        coords: first.coords,
                        routeType: segments[segmentIndex].routeType,
                    },
                    { panelType: "geopoint", geozone: point, i: point.i },
                    {
                        panelType: "segment",
                        geozone: { start: point, end },
                        i: second.i,
                        coords: second.coords,
                        routeType: segments[segmentIndex].routeType,
                    },
                ],
            );
            const nextTollRoadSegments = tollRoadSegments.filter((item) => item.segmentIndex !== activeSegmentId);
            nextTollRoadSegments.push(...(first.routeInfo.tollSegments || []));
            nextTollRoadSegments.push(...(second.routeInfo.tollSegments || []));
            markers.splice(markerIndex, 1, { geozone: point, i: point.i });
            segments.splice(segmentIndex, 2, ...[first, second]);
            const nextState = {
                segments,
                markers,
                elements: nextNavElements,
                sidebarOpened: true,
                mapClickDisabled: true,
                activeElements: { point: null, segments: null },
            };
            this.setState({ isLoading: false });
            return changeActiveElements(nextState);
        });
        this.polylinePointDraged = false;
    };

    addNewSegment = ({ target }) => {
        const latlng = target._latlng;
        this.removeSuggestedRoutes();
        this.handleMapClick({ latlng, isDraged: true });
    };

    polylineEvents = (type) => {
        const { selectGeozoneMode } = this.state;
        if (!selectGeozoneMode || selectGeozoneMode === "form") {
            return {};
        }
        return {
            mouseover: () => this.setState({ mouseOnLine: type }),
            mouseout: () => {
                setTimeout(() => {
                    this.setState({ selectGeozonePoint: undefined, mouseOnLine: false });
                }, 0);
            },
            click: () => {
                this.setGeozonePoint(this.state.selectGeozonePoint);
                this.setState({ selectGeozonePoint: undefined, mouseOnLine: false });
            },
        };
    };

    setGeozonePoint = (point) => {
        if (!point) {
            return;
        }
        this.setState({ isLoading: true });
        geocoder({ lng: point[0], lat: point[1] }).then((geozone) => {
            if (this.state.selectGeozoneMode === "start") {
                this.setState({ isLoading: false });
                this.setState({ startGeozone: geozone, selectGeozoneMode: "end" });
            } else {
                const segmentCoords = [this.state.startGeozone.location.coordinates, geozone.location.coordinates];
                return getRoute(segmentCoords, { routeOptions: this.props.routeOptions }).then((data) => {
                    this.setState({ isLoading: false });
                    this.setState({
                        routeToFix: data?.concatedCoords || [],
                        endGeozone: geozone,
                        selectGeozoneMode: "form",
                    });
                });
            }
        });
    };

    renderPolyline = (item) => {
        const tollSegments = this.props.tollRoadSegments.filter((tollSegment) => tollSegment.segmentIndex === item.i);
        const defProps = {
            weight: 5,
            eventHandlers: {
                mouseover: (e) => {
                    if (this.polylinePointDraged) {
                        return;
                    }
                    this.setState({ polylinePoint: [e.latlng.lat, e.latlng.lng] });
                    this.polylinePointSegmentId = item.i;
                },
            },
        };
        const polylines = [
            {
                ...defProps,
                eventHandlers: { ...defProps.eventHandlers, ...this.polylineEvents(item.i) },
                key: item.i,
                positions: item.coords,
                ...(item.routeType === routeTypesEnum.EmptyMileage ? { ...planDashLineStyle } : { ...planLineStyle }),
                ...(this.state.selectGeozoneMode ? { dashArray: undefined } : {}),
            },
        ];

        if (tollSegments.length > 0 && !this.state.selectGeozoneMode && this.state.bidFilters.tollRoads) {
            tollSegments.forEach((tollSegment, i) => {
                polylines.push({
                    ...defProps,
                    key: `${item.i}_${i}`,
                    positions: item.coords.slice(tollSegment.startIndex, tollSegment.endIndex),
                    color: tollSegment.color,
                });
            });
        }
        return polylines.map((polylineProps) => (
            <Polyline key={polylineProps.key} ref={(el) => (this.lines[item.i] = el)} {...polylineProps} />
        ));
    };

    renderPolylines = () => {
        if (!this.props.segments) {
            return null;
        }
        const { navElements } = this.props;
        const segments = navElements ? navElements.filter((item) => item.panelType === "segment") : [];
        if (segments) {
            this.props.segments.forEach((s) => {
                const findSegment = segments.find((el) => el.i === s.i);
                findSegment && (findSegment.coords = s.coords);
            });
        }
        return segments.map(this.renderPolyline);
    };

    onPolylineMouseOut = () => {
        if (this.polylinePointDraged) {
            return;
        }
        this.setState({ polylinePoint: null });
        this.polylinePointSegmentId = null;
    };

    renderPolylineDragMarker = () => {
        if (!this.state.polylinePoint) {
            return null;
        }
        const props = {
            position: this.state.polylinePoint,
            icon: divIcon({ className: "route-icon route-icon--between", iconSize: [15, 15] }),
            draggable: true,
            eventHandlers: {
                mouseout: this.onPolylineMouseOut,
                dragend: this.addNewSegment,
                dragstart: () => (this.polylinePointDraged = true),
                drag: (e) => {
                    this.removeSuggestedRoutes();
                    this.loadRoutesPreview(e);
                },
            },
        };
        return <Marker {...props} />;
    };

    onSelectGeopoint = (geopoint) => {
        const { coordinates } = geopoint.location;
        if (coordinates.length) {
            const { mergeVp } = this.props;
            mergeVp({ center: [...coordinates].reverse() });
        }
        this.setState({ geopoint });
    };

    confirmMarker = () => {
        const { geopoint } = this.state;
        if (geopoint.location.coordinates.length) {
            this.addGeopoint(geopoint);
        } else {
            alert({ message: "Выберите более точный адрес", type: "warning" });
        }
    };

    renderInputAddress = () => {
        const { shouldResize } = this.props;
        if (shouldResize) {
            return null;
        }
        return (
            <>
                <div className="asided-modal__map-hint" title="Через нее будет проложен маршрут">
                    <AddressInput
                        autofocus
                        onSelectGeopoint={this.onSelectGeopoint}
                        geopoint={this.state.geopoint}
                        isLoading={this.state.isLoading}
                    />
                </div>
                <div className="asided-modal__map-hint-btn-wrap">
                    <div className="map__button-wrap">
                        <div className="btn btn-brand map__submit-btn" onClick={this.confirmMarker}>
                            Подтвердить точку
                        </div>
                    </div>
                </div>
            </>
        );
    };

    renderSelectedGeopoint = () => {
        const geopoint = this.state.geopoint;
        if (!geopoint || !geopoint.location) {
            return null;
        }
        const { coordinates } = geopoint.location;

        const inverted = [coordinates[1], coordinates[0]];
        const options = {
            position: inverted,
            icon: divIcon({ className: "route-icon route-icon--between", iconSize: [15, 15] }),
        };
        return coordinates?.length ? <Marker {...options} /> : null;
    };

    renderFuelIcon = (item) => {
        const { resource, gasStationType } = item;

        if (resource && resource.iconUrl) {
            return `<img src=${resource.iconUrl} class="route-icon__refueling-logo" />`;
        } else if (gasStationType === gasStationTypesEnum.Rosneft) {
            return '<img src="/images/refuelings/rosneft.png" class="route-icon__refueling-logo" />';
        } else {
            return null;
        }
    };

    renderRouteMarker = (item, firstLoadPointIndex, index) => {
        const isBetweenGeopoint = item.panelType === "geopoint" || !item.panelType;

        const className = cn("route-icon", {
            "route-icon--between": isBetweenGeopoint,
            "route-icon--refueling": item.panelType === "gasstation" && !item.resource?.iconUrl,
            "route-icon--refueling-wrap": item.panelType === "gasstation" && item.resource?.iconUrl,
        });

        const { coordinates } = item.geozone.location;
        const inverted = [coordinates[1], coordinates[0]];

        const markerContent = () => {
            if (isStartBidPoint(item.geozone.type)) {
                return START_POINT_LETTER;
            }
            if (item.geozone.type === bidPointTypesEnum.ServicePoint) {
                return "<i class='icon-uEA97-wrench' />";
            } else {
                return pointLetter(index, firstLoadPointIndex);
            }
        };

        const iconOptions = isBetweenGeopoint
            ? { iconSize: [15, 15] }
            : item.panelType === "gasstation"
              ? { iconSize: [26, 26], html: this.renderFuelIcon(item) }
              : item.panelType === "itemchange"
                ? { iconSize: [22, 22], className: "map-icon--item-changes" }
                : {
                      iconSize: [30, 30],
                      html: markerContent(),
                  };
        const id = item.id || item.i;
        const options = {
            id,
            position: inverted,
            icon: divIcon({ className, ...iconOptions }),
            draggable: isBetweenGeopoint,
            eventHandlers: {
                dragend: (e) => this.updateBetweenPoint(e, id),
                dragstart: () => (this.polylinePointDraged = true),
                drag: (e) => {
                    this.removeSuggestedRoutes();
                    this.loadRoutesPreview(e);
                },
                dblclick: () => {
                    if (!isBetweenGeopoint) {
                        return;
                    }
                    this.removeSuggestedRoutes();
                    setTimeout(() => {
                        const { routeOptions } = this.props;
                        const { elements } = clearGasStations({ elements: this.props.navElements });
                        const itemIndex = elements.findIndex((el) => el.i === item.i);
                        const prevSegment = elements[itemIndex - 1] || {};
                        return this.props
                            .getRouteWithoutElement({ elements, item }, { routeOptions })
                            .then((res) => this.props.calcDeleted({ ...res, prevSegment }));
                    }, 0);
                },
            },
        };
        return <Marker key={id} {...options} />;
    };

    renderRouteMarkers = (markers = []) => {
        let index = -1;
        const routePointsHtml = [];
        const supportPointsHtml = [];
        const refuelingPointsHtml = [];
        let firstLoadPointIndex = -1;
        markers?.some((item) => {
            if (item.panelType === "bidpoint" && item.geozone.type !== mapObjectTypesEnum.StartBidPoint) {
                firstLoadPointIndex++;
            }
            return [bidPointTypesEnum.LoadPoint, bidPointTypesEnum.UnloadPoint].includes(item.type);
        });
        markers.forEach((item) => {
            if (item.panelType === "bidpoint") {
                !isStartBidPoint(item.geozone.type) && index++;
                routePointsHtml.push(this.renderRouteMarker(item, firstLoadPointIndex, index));
            } else if (item.panelType === "gasstation") {
                refuelingPointsHtml.push(this.renderRouteMarker(item, index));
            } else {
                supportPointsHtml.push(this.renderRouteMarker(item, index));
            }
        });
        return (
            <>
                <MarkerCluster maxClusterRadius={20}>{routePointsHtml}</MarkerCluster>
                {refuelingPointsHtml}
                {supportPointsHtml}
            </>
        );
    };

    renderBody = () => {
        const {
            markers,
            emergencyAreaPoints,
            routeControlPoints,
            platonPoints,
            postamatPoints,
            paymentPoints,
            viewport,
            unstableZones,
            regionDryingPeriods,
            roadRestrictions,
            roles,
            regionDryingPeriodIds,
            bidId,
        } = this.props;
        if (this.state.selectGeozoneMode) {
            return this.renderPolylines();
        }

        return (
            <>
                {this.renderPolylines()}
                {this.renderPolylineDragMarker()}
                {this.renderRouteMarkers(markers)}
                {this.state.bidFilters.emergencyAreaPoints && (
                    <EmergencyAreaPoints emergencyAreaPoints={emergencyAreaPoints} />
                )}
                {this.state.bidFilters.routeControlPoints && (
                    <RouteControlPoints routeControlPoints={routeControlPoints} />
                )}
                {this.state.bidFilters.platonPoints && <PlatonPoints platonPoints={platonPoints} />}
                {this.state.bidFilters.postamats && <Postamats postamats={postamatPoints} />}
                {this.state.bidFilters.unstableZones && canManageOrganization(roles) && (
                    <UnstableZones unstableZones={unstableZones} />
                )}
                {this.state.bidFilters.paymentPoints && <PaymentPoints points={paymentPoints} zoom={viewport.zoom} />}
                {isRegionDryingPeriodsEnabledForClients && this.state.bidFilters.regionDryingFilters && (
                    <RegionDryingPeriods
                        regionDryingPeriods={regionDryingPeriods}
                        regionDryingPeriodIds={regionDryingPeriodIds}
                        currentBid={bidId}
                    />
                )}
                <RoadRestrictions zoom={viewport.zoom} points={roadRestrictions} filters={this.state.bidFilters} />
                <BidRoadRestrictions route={{ segments: this.props.segments }} filters={this.state.bidFilters} />
            </>
        );
    };

    renderFixRouteBidForm = () => {
        const { bidId, routeOptions, defaultRouteOptions, organization } = this.props;
        const { selectGeozoneMode, startGeozone, endGeozone, routeToFix } = this.state;
        if (!selectGeozoneMode && !startGeozone) {
            return null;
        }
        return (
            <FixRouteBidForm
                bidId={bidId}
                routeOptions={routeOptions}
                routerType={organization.routerType}
                defaultRouteOptions={defaultRouteOptions}
                startGeozone={startGeozone}
                endGeozone={endGeozone}
                routeToFix={routeToFix}
                selectGeozoneMode={selectGeozoneMode}
                cancelSelectRange={() =>
                    this.setState({
                        selectGeozoneMode: undefined,
                        startGeozone: undefined,
                        endGeozone: undefined,
                        routeToFix: undefined,
                    })
                }
            />
        );
    };

    renderBidDataFilter = () => {
        const { availableFeatureModules, roadRestrictionTypes, roles } = this.props;
        const { bidFilters } = this.state;
        return (
            <BidDataFilter
                inEditRoute
                roles={roles}
                filters={bidFilters}
                toggleGroupFilter={this.toggleGroupFilter}
                toggleFilter={this.toggleFilter}
                availableFeatureModules={availableFeatureModules}
                roadRestrictionTypes={roadRestrictionTypes}
            />
        );
    };

    roadRestrictionList = () => (
        <RoadRestrictionsList route={this.props} routeControlPoints={this.props.routeControlPoints} />
    );

    render() {
        const { viewport, tileUrls } = this.props;
        const { selectGeozonePoint } = this.state;
        return (
            <>
                {this.state.isLoading && (
                    <div className="loader-snippet--map-loader">
                        <Loader />
                    </div>
                )}
                <LeafletMap
                    ref={(map) => (this.map = map)}
                    mapRef={this.map}
                    viewport={viewport}
                    tileUrls={tileUrls}
                    onViewportChanged={this.handleViewportChange}
                    zoomControl={false}
                    maxZoom={18}
                >
                    <MapEventHandler
                        onClick={this.handleMapClick}
                        setLinePoint={(selectGeozonePoint) => this.setState({ selectGeozonePoint })}
                        lines={this.lines}
                        mouseOnLine={this.state.mouseOnLine}
                    />

                    <MapControl position="topright">
                        <div className="d-flex flex-column gap-1 align-items-end">
                            {this.renderBidDataFilter()}
                            {this.roadRestrictionList()}
                        </div>
                    </MapControl>
                    <MapControl position="bottomleft">{this.renderFixRouteBidBtn()}</MapControl>
                    {selectGeozonePoint && (
                        <CircleMarker
                            center={[selectGeozonePoint[1], selectGeozonePoint[0]]}
                            {...taskMarkerStyle}
                            interactive={false}
                        />
                    )}
                    {this.renderBody()}
                    {this.renderFixRouteBidForm()}
                    {this.renderSelectedGeopoint()}
                </LeafletMap>
                {this.renderInputAddress()}
            </>
        );
    }
}

const mapStateToProps = (state) => ({
    platonPoints: state.map.platonPoints,
    postamatPoints: state.map.postamatPoints,
    paymentPoints: state.map.paymentPoints,
    roadRestrictions: state.map.roadRestrictions,
    roadRestrictionTypes: state.map.roadRestrictionTypes,
    regionDryingPeriods: state.map.regionDryingPeriods,
});

export default connect(mapStateToProps, {})(CreateRoute);

const MapEventHandler = (props) => {
    const map = useMap();
    useMapEvent("click", props.onClick);
    useMapEvent("mousemove", ({ latlng }) => {
        if (!props.mouseOnLine) {
            return;
        }

        const mapPoint = props.lines[props.mouseOnLine].closestLayerPoint(map.latLngToLayerPoint(latlng));
        const mapLatLngPoint = map.layerPointToLatLng(mapPoint);
        props.setLinePoint([mapLatLngPoint.lng, mapLatLngPoint.lat]);
    });
    return null;
};
